import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import Modal from "../../Modal/Modal";
import TableAdv from "../../Table/TableAdv/TableAdv";
import SmartTooltip from "../../Info/SmartTooltip";
import Backdrop from "../../Backgrounds/Backdrop";
import LogoLoader from "../../Loading/LogoLoader";
import { toTitleCase } from "../../../utils/common";



export default function BdmJobsModal({ isOpen, onClose, data, employeeId, totalCount,
  page,
  limit,
  setPage,
  handleLimitChange,
  loading,
  onSubmissionClick,
  submissionClickData,
  subPage,
  subLimit,
  subSetPage,
  subHandleLimitChange,
}) {

  const [modelOpen, setIsModelOpen] = useState(false)
  const [totalsubDataCount, setTotalsubDataCount] = useState([]);
  const [subName, setSubName] = useState()

  // ✅ Get employee name from filtered data
  const employeeName = useMemo(() => {
    const match = Array.isArray(data)
      ? data.find(
        (item) => String(item?.addedBy?.uiid) === String(employeeId)
      )
      : null;

    if (!match) return String(employeeId ?? "");

    return `${match?.addedBy?.firstName || ""} ${match?.addedBy?.lastName || ""
      }`.trim();
  }, [data, employeeId]);

  // ✅ Build rows
  const rows = useMemo(() => {
    const list = Array.isArray(data) ? data : [];

    return list
      .filter(
        (item) => String(item?.addedBy?.uiid) === String(employeeId)
      )
      .map((job) => ({
        jobPostId: job?.uiid ?? "",
        jobTitle: toTitleCase(job?.jobTitle) || "-",
        companyName: toTitleCase(job?.companyName) || "-",
        jobOpenDate: job?.addedOn
          ? dayjs(job.addedOn).format("MMM DD, YYYY")
          : "",
        internalSubmissions: job?.internalSubmission ?? 0,
        clientSubmissions: job?.clientSubmission ?? 0,
        aggrements: job?.aggrements ?? 0,
        interviewRound1: job?.interviewRound1 ?? 0,
        interviewRound2: job?.interviewRound2 ?? 0,
        interviewRound3: job?.interviewRound3 ?? 0,
        placements: job?.placements ?? 0,
        addedBy:job?.addedBy?.uiid
      }))
      .sort((a, b) => {
        const at = dayjs(a.jobOpenDate).valueOf() || 0;
        const bt = dayjs(b.jobOpenDate).valueOf() || 0;

        if (bt !== at) return bt - at;
        return String(a.jobTitle || "").localeCompare(String(b.jobTitle || ""));
      });
  }, [data, employeeId]);

  const subminsRows = useMemo(() => {
    const list = Array.isArray(submissionClickData) ? submissionClickData : [];

    return list
      .map((item) => ({
        submissionId: item?.uiid ?? "",
        candidateName: toTitleCase(item?.candidateName) || "-",
        jobTitle: toTitleCase(item?.jobTitle) || "-",
        companyName: toTitleCase(item?.companyName) || "-",
        //status: item?.status ?? "",
        submittedDate: item?.submittedDate
          ? dayjs(item.submittedDate).format("MMM DD, YYYY")
          : "",
      }))
      .sort((a, b) => {
        const at = dayjs(a.submittedDate).valueOf() || 0;
        const bt = dayjs(b.submittedDate).valueOf() || 0;

        if (bt !== at) return bt - at;

        return String(a.candidateName || "").localeCompare(
          String(b.candidateName || "")
        );
      });
  }, [submissionClickData]);

  const columns = useMemo(
    () => [
      {
        key: "jobTitle",
        label: "Job Title",
        width: "min-w-[260px]! max-w-[340px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.jobTitle}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.jobTitle}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        width: "min-w-[220px]! max-w-[280px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.companyName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.companyName}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "jobOpenDate",
        label: "Added On",
        width: "min-w-[160px]!",
      },
      {
        key: "internalSubmissions",
        label: "Internal Submissions",
        width: "min-w-[160px]!",
        render: (row) => row?.internalSubmissions ? (
          <p className="text-info-400 cursor-pointer" onClick={() => {
            setSubName("Internal Submission")
            setIsModelOpen(true)
            setTotalsubDataCount(row?.internalSubmissions)

            onSubmissionClick?.({
              id: row?.jobPostId,
              name: "Internal Submission",
              recruitId : row?.addedBy
            });
          }}>{row?.internalSubmissions}</p>
        ) : row?.internalSubmissions
      },
      {
        key: "clientSubmissions",
        label: "Client Submissions",
        width: "min-w-[160px]!",
        render: (row) => row?.clientSubmissions ? (
          <p className="text-info-400 cursor-pointer" onClick={() => {

            setIsModelOpen(true)
            setTotalsubDataCount(row?.clientSubmissions)
            onSubmissionClick?.({
              id: row?.jobPostId,
              name: "Client Submissions",
              recruitId : row?.addedBy
            });
            setSubName("Client Submissions")
          }}>{row?.clientSubmissions}</p>
        ) : row?.clientSubmissions
      },
      {
        key: "aggrements",
        label: "Aggrements",
        width: "min-w-[160px]!",
        render: (row) => row?.aggrements ? (
          <p className="text-info-400 cursor-pointer" onClick={() => {

            setIsModelOpen(true)
            setTotalsubDataCount(row?.aggrements)
            onSubmissionClick?.({
              id: row?.jobPostId,
              name: "Aggrements",
              recruitId : row?.addedBy
            });
            setSubName("Aggrements")
          }}>{row?.aggrements}</p>
        ) : row?.aggrements
      },
      {
        key: "interviewRound1",
        label: "Interview Round 1",
        width: "min-w-[160px]!",
        render: (row) => row?.interviewRound1 ? (
          <p className="text-info-400 cursor-pointer" onClick={() => {

            setIsModelOpen(true)
            setTotalsubDataCount(row?.interviewRound1)
            onSubmissionClick?.({
              id: row?.jobPostId,
              name: "Interview Round 1",
              recruitId : row?.addedBy
            });
            setSubName("Interview Round 1")
          }}>{row?.interviewRound1}</p>
        ) : row?.interviewRound1
      },
      {
        key: "interviewRound2",
        label: "Interview Round 2",
        width: "min-w-[160px]!",
        render: (row) => row?.interviewRound2 ? (
          <p className="text-info-400 cursor-pointer" onClick={() => {

            setIsModelOpen(true)
            setTotalsubDataCount(row?.interviewRound2)
            onSubmissionClick?.({
              id: row?.jobPostId,
              name: "Interview Round 2",
              recruitId : row?.addedBy
            });
            setSubName("Interview Round 2")
          }}>{row?.interviewRound2}</p>
        ) : row?.interviewRound2
      },
      {
        key: "interviewRound3",
        label: "Interview Round 3",
        width: "min-w-[160px]!",
        render: (row) => row?.interviewRound3 ? (
          <p className="text-info-400 cursor-pointer" onClick={() => {

            setIsModelOpen(true)
            setTotalsubDataCount(row?.interviewRound3)
            onSubmissionClick?.({
              id: row?.jobPostId,
              name: "Interview Round 3",
              recruitId : row?.addedBy
            });
            setSubName("Interview Round 3")
          }}>{row?.interviewRound3}</p>
        ) : row?.interviewRound3
      },
      {
        key: "placements",
        label: "Placements",
        width: "min-w-[160px]!",
        render: (row) => row?.placements ? (
          <p className="text-info-400 cursor-pointer" onClick={() => {

            setIsModelOpen(true)
            setTotalsubDataCount(row?.placements)
            onSubmissionClick?.({
              id: row?.jobPostId,
              name: "Placements",
              recruitId : row?.addedBy
            });
            setSubName("Placements")
          }}>{row?.placements}</p>
        ) : row?.placements
      },

    ],
    []
  );
  const submissionColumns = useMemo(
    () => [
      { key: "candidateName", label: "Candidate Name" },
      {
        key: "submittedDate",
        label: "Submitted Date",
      },
      {
        key: "jobTitle",
        label: "Job Title",
        render: (row) => (
          <SmartTooltip description={row?.jobTitle} noicon>
            <div className="line-clamp-1">{row?.jobTitle}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        render: (row) => (
          <SmartTooltip description={row?.companyName} noicon>
            <div className="line-clamp-1">{row?.companyName}</div>
          </SmartTooltip>
        ),
      },
    ],
    []
  );

  const jobsAddedCount = useMemo(() => {
    if (!data || !totalCount) return 0;

    let total = 0;

    data.forEach(item => {
      const bdmId = item?.addedBy?.uiid;

      const match = totalCount.find(
        t => String(t?.addedBy?.uiid) === String(bdmId)
      );

      if (match) {
        total = Number(match?.jobsAdded || 0);
      }
    });

    return total;
  }, [data, totalCount]);

  return (
    <div>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`${toTitleCase(employeeName)} - Jobs List`}
        width="max-w-8xl"
        bodyClassName="p-0.5 flex-1 overflow-auto"
      >
        <div className="flex-1 overflow-auto flex gap-4 p-0.5">
          <TableAdv
            showSlNo
            columns={columns}
            rows={rows}
            tableId="treeHierarchyBdmJobs"
            serverPagination={true}
            totalCount={jobsAddedCount}
            page={page}
            limit={limit}
            onPaginationChange={setPage}
            onLimitChange={handleLimitChange}
            toolbarRight={{
              search: true,
              filter: false,
              download: false,
              viewToggle: false,
              fullScreen: false,
              add: [],
            }}
            toolbarLeft={{ add: [] }}
          />
          <Backdrop isOpen={loading} zIndex={5}>
            <LogoLoader />
          </Backdrop>
        </div>

      </Modal>
      <Modal
        isOpen={modelOpen}
        onClose={() => {
          setIsModelOpen(false)
          setTotalsubDataCount()
          subSetPage(1)
          //canHandleLimitChange(25)
        }}
        title={subName}
        titleColor={'primary'}
        width="max-w-7xl"
      >
        <TableAdv
          showSlNo
          rows={subminsRows}
          columns={submissionColumns}
          serverPagination={true}
          totalCount={totalsubDataCount}
          page={subPage}
          limit={subLimit}
          onPaginationChange={subSetPage}
          onLimitChange={subHandleLimitChange}
        />

        {/* <Backdrop isOpen={loading} zIndex={53}>
                  <LogoLoader />
                </Backdrop> */}
      </Modal>
    </div>

  );
}
